import SearchBox from 'components/molecules/SearchBox';
import { useConvertPercentageToPxWidth, useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import LensesTabsContainer from '../LensesTabsContainer';
import DimSection from '../DimSection';
import { AttributeTypeItem } from 'types/pricingSimulator';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import { useFetchComparisonTableData } from 'hooks/pricingSimulator/analytics/useFetchComparisonTableData';
import { pricingAnalyticsUrls } from 'config/urls';
import { getComparisonTableColumns } from 'utils/getPricingSimulatorTableColumns';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { CustomTable } from '../ScenarioTableWrapper';
import Footer from '../Footer';
import { errorStatuses, pollingStatuses } from 'components/organism/PricingSimulator/UpdateOrViewPricingSimulator';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';
import { ConfigKeys } from 'utils/Enums';
const defaultDimSectionWidth = 35;

const comparisonTableContainerId = 'comparison-table-container';

const ComparisonTableContainer = () => {
    const { appConfig } = useContext(UserContext);
    const pricingSimulatorConfig = getConfig('pricingSimulator', appConfig);
    const commonConfig = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;

    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const defaultDim1DropdownItem = dimensionConfigData?.defaultDim1DropdownItem ?? {
        dimension: '',
        label: '',
        value: '',
    };
    const {
        updateComparisonProperty,
        scenarioComparisonState,
        selectedDimensionListValues,
        isDropdownAtLowestLevel,
        selectedReportInfo,
        isUpdate,
        loggedInUser,
        filters,
        showOldValue,
    } = useContext(PricingAnalyticsDataContext);

    //states
    const [searchKey, setSearchKey] = useState<string>('');
    const [sortPopupKey, setSortPopupKey] = useState(null);

    //width and height refs
    const dimensionSectionWidth = useRef<number>(defaultDimSectionWidth);
    const dimSectionWidth = useRef<number | null>(0);
    const lensesSectionWidth = useRef<number | null>(0);
    const mainSectionHeight = useRef<number | null>(0);

    //this hook with give height to fill the screen height
    mainSectionHeight.current = useFillHeightToFitScreen({
        id: comparisonTableContainerId,
        height: mainSectionHeight.current,
        buffer: -300, //for padding at the bottom, change to 16 once testing is done
        // triggerDummyRender: true,
    });

    //this hook with convert percentage to width in px and return
    dimSectionWidth.current = useConvertPercentageToPxWidth({
        id: 'comparison-bottom-table-container',
        width: dimSectionWidth.current,
        percentageNumber: dimensionSectionWidth.current,
        triggerDummyRender: true,
    });

    //this hook with convert percentage to width in px and return
    lensesSectionWidth.current = useConvertPercentageToPxWidth({
        id: 'comparison-bottom-table-container',
        width: lensesSectionWidth.current,
        percentageNumber: 100 - dimensionSectionWidth.current,
        // triggerDummyRender: true,
    });
    //footer texts
    const footerFirstText =
        (scenarioComparisonState?.tableState?.data?.length ?? 0) > 0
            ? `Showing ${scenarioComparisonState?.tableState?.data?.length?.toLocaleString()} of ${scenarioComparisonState?.tableState?.count?.toLocaleString()} records`
            : '';
    const footerFourthText = selectedReportInfo?.updatedAt ? `Last Updated: ${selectedReportInfo?.updatedAt}` : '';

    const footerThirdText = selectedReportInfo?.createdBy ? `Created by: ${selectedReportInfo?.createdBy}` : '';

    const isErrorStatus = errorStatuses.includes(selectedReportInfo?.baseScenario?.status || '');
    const setUpOrInprogressLoading = pollingStatuses.includes(selectedReportInfo?.baseScenario?.status || '');
    const isActive = selectedReportInfo?.baseScenario?.status === 'ACTIVE';

    //table variables
    const tableVariables = useMemo(() => {
        return {
            attribute_types: selectedDimensionListValues,
            kpi_id: scenarioComparisonState?.selectedKpiItem?.id,
            report_id: selectedReportInfo?.reportId,
            scenario_ids: scenarioComparisonState?.selectedScenarios?.map((item) => item.scenarioId),
            sort_by: scenarioComparisonState?.tableState?.sortObject?.sortBy,
            sort_type: scenarioComparisonState?.tableState?.sortObject?.sortType,
            sort_order: scenarioComparisonState?.tableState?.sortObject?.sortingOrder,
            is_update: isUpdate,
            user_name: loggedInUser,
            limit: pricingSimulatorConfig?.comparisonTableLimit,
            offset: scenarioComparisonState?.tableState?.offset,
            is_lowest: isDropdownAtLowestLevel,
            search_keyword: scenarioComparisonState?.tableState?.search,
            filters: filters,
        };
    }, [
        scenarioComparisonState?.tableState,
        scenarioComparisonState?.selectedScenarios,
        scenarioComparisonState?.selectedKpiItem?.id,
        filters,
    ]);

    // GET COMPARISON TABLE DATA
    const { loading: tableDataLoading, error: tableDataError } = useFetchComparisonTableData({
        method: 'post',
        queryConfig: { enabled: Boolean(tableVariables?.kpi_id) },
        queryKey: ['pa-comparison-table-data', tableVariables],
        url: `${pricingAnalyticsUrls.getHeatmapTableDataUrl}`,
        variables: {
            ...tableVariables,
        },
        extras: { scenarioComparisonState },
        updateComparisonProperty,
    });
    //resize section when more dims are added
    //usememo runs in render phase hence using this
    useMemo(() => {
        dimSectionWidth.current = 0;
        lensesSectionWidth.current = 0;
        dimensionSectionWidth.current = Math.max(
            defaultDimSectionWidth,
            Math.ceil(selectedDimensionListValues.length * 14),
        );
    }, [selectedDimensionListValues]);

    const memoisedColumns = useMemo(() => {
        const predefinedColumns =
            (scenarioComparisonState?.tableState?.data?.length ?? 0) > 0
                ? scenarioComparisonState?.tableState?.dimAttributes?.map((item) => {
                      return {
                          label: item?.label || '',
                          key: item?.value || '',
                          sortKey: item?.value || '',
                      };
                  })
                : [];
        const sortOrder = scenarioComparisonState?.tableState?.sortObject?.sortingOrder;
        return getComparisonTableColumns(
            scenarioComparisonState?.tableState ?? {},
            predefinedColumns ?? [],
            2 + selectedDimensionListValues?.length,
            dimSectionWidth.current || 528,
            showOldValue,
            {
                sortPopupKey,
                sortBy: scenarioComparisonState?.tableState?.sortObject?.sortBy || 0,
                sortType: scenarioComparisonState?.tableState?.sortObject?.sortType || 0,
                sortOrder: sortOrder === 'asc' ? 'ascend' : sortOrder === 'desc' ? 'descend' : null,
            },
            setSortPopupKey,
            updateComparisonProperty,
            selectedDimensionListValues,
            decimalForNonPercentage,
            decimalForPercentage,
        );
    }, [scenarioComparisonState?.tableState?.data, dimSectionWidth.current, showOldValue, sortPopupKey]);

    useEffect(() => {
        setSearchKey(scenarioComparisonState?.tableState?.search ?? '');
    }, [scenarioComparisonState?.tableState?.search]);

    //called when clicked on search icon or enter
    const onSearch = (val: string) => {
        updateComparisonProperty('tableState', {
            ...scenarioComparisonState?.tableState,
            offset: 0,
            search: val,
        });
    };

    //called when dimension popup is closed
    function onDimensionPopupClose(tempSelectedDimensionList: AttributeTypeItem[]) {
        let tempDimensionList = tempSelectedDimensionList;
        let values = tempDimensionList?.map((list) => list.value);

        //if nothing is selected, default to dim1 - product
        if (!values?.length) {
            tempDimensionList = [defaultDim1DropdownItem];
            values = tempDimensionList.map((item) => item.value);
        }
        // return sendNotification('error', 'Minimum 2 Attributes should be selected');

        updateComparisonProperty('tableState', {
            ...scenarioComparisonState?.tableState,
            dimAttributes: tempDimensionList,
            offset: 0, //reset offset
            sortObject: {
                //update sort function if this column is active

                sortBy: selectedDimensionListValues.includes(
                    scenarioComparisonState?.tableState?.sortObject?.sortBy || '',
                )
                    ? values?.includes(scenarioComparisonState?.tableState?.sortObject?.sortBy ?? '')
                        ? scenarioComparisonState?.tableState?.sortObject?.sortBy ?? ''
                        : values?.[0]
                    : scenarioComparisonState?.tableState?.sortObject?.sortBy ?? '',
                sortingOrder: scenarioComparisonState?.tableState?.sortObject?.sortingOrder || 'asc',
                sortType: 'actual',
            },
        });
    }

    const commonLoading = tableDataLoading;

    function infiniteScrollOnNext(loading: boolean) {
        if (!loading) {
            const newOffset =
                pricingSimulatorConfig?.comparisonTableLimit + (scenarioComparisonState?.tableState?.offset ?? 0);
            updateComparisonProperty('tableState', {
                ...scenarioComparisonState?.tableState,
                offset: newOffset,
            });
        }
    }
    return (
        <div className="mb-4">
            <div className="flex my-5 items-center justify-between">
                <p className="text-blackAlt font-sm font-medium"> Level Heatmap - Multiple Scenarios</p>
                <SearchBox onSearch={onSearch} search={searchKey} setSearch={setSearchKey} />
            </div>
            <div
                className="border bg-backgroundGreyAlt border-backgroundLightGrey rounded-t-[8px]"
                id="comparison-bottom-table-container"
            >
                {/* DIM DROPDOWN/LENS SECTION */}
                <div className="w-full flex items-center">
                    <div className="py-1 px-4 " style={{ width: dimSectionWidth.current || 'auto' }}>
                        <DimSection
                            onPopupClose={onDimensionPopupClose}
                            dimensionInfo={{ dimAttributes: scenarioComparisonState?.tableState?.dimAttributes ?? [] }}
                            selectedDimensionListValues={selectedDimensionListValues}
                        />
                    </div>
                    <div
                        className="border-l border-l-backgroundLightGrey py-3 pl-4 pr-2"
                        style={{ width: lensesSectionWidth.current || 'auto' }}
                    >
                        <LensesTabsContainer
                            activeLensKey={scenarioComparisonState?.selectedKpiItem?.id ?? 0}
                            lenses={[
                                {
                                    key: scenarioComparisonState?.selectedKpiItem?.id ?? 0,
                                    label: scenarioComparisonState?.selectedKpiItem?.label ?? '',
                                },
                            ]}
                        />
                    </div>
                </div>
                {/* TABLE SECTION */}
                <div id={comparisonTableContainerId}>
                    {commonLoading && !scenarioComparisonState?.tableState?.offset ? (
                        <div
                            className=" flex flex-col items-center "
                            style={{
                                height: mainSectionHeight.current || 'auto',
                            }}
                        >
                            <Loader />
                        </div>
                    ) : !commonLoading && tableDataError ? (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: mainSectionHeight.current || 'auto',
                            }}
                        >
                            <ErrorBox msg={String(tableDataError)} />
                        </div>
                    ) : (
                        <>
                            <InfiniteScroller
                                data={scenarioComparisonState?.tableState?.data ?? []}
                                loading={commonLoading}
                                hasMore={
                                    (scenarioComparisonState?.tableState?.data?.length ?? 0) <
                                        (scenarioComparisonState?.tableState?.count ?? 0) || commonLoading
                                }
                                height={
                                    scenarioComparisonState?.tableState?.data && !tableDataError
                                        ? mainSectionHeight.current ?? undefined
                                        : 'auto'
                                }
                                //height={200}
                                //     loader={true}
                                onNext={() => infiniteScrollOnNext(tableDataLoading)}
                            >
                                <CustomTable
                                    dimLength={selectedDimensionListValues?.length + 2}
                                    showSorterTooltip={false}
                                    onChange={(pagination, filter, sort: any, extra) => {
                                        //if fixed is not present, it means its coming from kpi column
                                        //for kpi column, show sorting popup
                                        if (!sort?.column?.fixed) {
                                            setSortPopupKey(sort?.column?.sortKey);
                                            return;
                                        }
                                        //only for dim1 and dim2
                                        if (sort?.order) {
                                            const sortMapping: any = {
                                                descend: 'desc',
                                                ascend: 'asc',
                                            };
                                            updateComparisonProperty('tableState', {
                                                ...scenarioComparisonState?.tableState,
                                                sortObject: {
                                                    sortBy: sort?.column?.sortKey,
                                                    sortingOrder: sortMapping?.[sort?.order] || 'asc',
                                                    sortType: 'actual',
                                                },
                                                offset: 0,
                                            });

                                            //default it to dim1 column on cancelling sorting
                                        } else {
                                            updateComparisonProperty('tableState', {
                                                ...scenarioComparisonState?.tableState,
                                                sortObject: {
                                                    sortBy: selectedDimensionListValues?.[0] || '',
                                                    sortingOrder: 'asc',
                                                    sortType: 'actual',
                                                },
                                                offset: 0,
                                            });
                                        }
                                    }}
                                    //loading={commonLoading}
                                    sticky={{
                                        getContainer: () =>
                                            document.getElementsByClassName(
                                                'infinite-scroll-component',
                                            )?.[0] as HTMLElement,
                                    }} //adding x axis scrollbar on table
                                    style={{
                                        borderRadius: '4px 0px 0px 4px',
                                        opacity: commonLoading ? '0.3' : 1,
                                        pointerEvents: commonLoading ? 'none' : 'auto',
                                    }}
                                    locale={{
                                        emptyText: tableDataError ? <ErrorBox msg={String(tableDataError)} /> : '',
                                    }}
                                    columns={memoisedColumns}
                                    dataSource={scenarioComparisonState?.tableState?.data}
                                    pagination={false}
                                    scroll={{ x: '100%' }}
                                />
                            </InfiniteScroller>
                        </>
                    )}
                    <Footer
                        type={
                            isErrorStatus
                                ? 'error'
                                : setUpOrInprogressLoading
                                ? 'inProgress'
                                : isActive
                                ? 'success'
                                : 'default'
                        }
                        items={[footerFirstText, '', footerThirdText, footerFourthText]}
                    />
                </div>
            </div>
        </div>
    );
};

export default ComparisonTableContainer;
